import Field, { AggregationType, FieldType, fieldToOutputType } from "@dto/fields";
import { FieldColumn, Line, SUMMABLE_TYPES } from "@dto/records";
import { Summary, View } from "@dto/views";
import { valueToString } from "@utils/types";
import { TFunction } from "i18next";
import { useModelStore } from "src/store";

// Aggregations that can be selected for a column summary, in display order.
export const SUMMARY_AGGREGATIONS: AggregationType[] = [
    AggregationType.SUM,
    AggregationType.AVG,
    AggregationType.MIN,
    AggregationType.MAX
];

// Aggregation applied when a column is added to the summaries.
export const DEFAULT_SUMMARY_AGGREGATION: AggregationType = AggregationType.SUM;

// summaryOutputType is the type of the values DISPLAYED in a column: the field
// type transformed by the column aggregation (a COUNT over any field displays
// integers, a CONCAT displays text...), link chains resolved to their final
// destination field. fieldByKeys defaults to the model store (same pattern as
// validAggregationByType).
function summaryOutputType(field: Field, aggregation: AggregationType | undefined, fieldByKeys?: Map<string, Field> | null): FieldType {
    const keys = fieldByKeys !== undefined ? fieldByKeys : useModelStore.getState().fieldByKeys;
    return fieldToOutputType(field, aggregation, keys ?? null, true);
}

// isSummable tells whether a column can be aggregated in the summary lines:
// columns whose displayed values are numeric — numeric fields, LINK fields
// whose final destination field is numeric (their cells display the
// per-record numeric aggregates, which the summaries aggregate in turn), and
// any column aggregated with COUNT. The summaries are computed by the backend
// over the displayed cells, so the column aggregation (view override, else
// the field default: columnAggregation in @dto/fields) decides, not the
// declared field type alone.
export function isSummable(field: Field | undefined | null, aggregation?: AggregationType, fieldByKeys?: Map<string, Field> | null): boolean {
    if (!field) {
        return false;
    }
    return SUMMABLE_TYPES.includes(summaryOutputType(field, aggregation, fieldByKeys));
}

// upsertSummary replaces the summary of the same path key, or appends it when
// the path key is not aggregated yet.
export function upsertSummary(summaries: Summary[] | null, summary: Summary): Summary[] {
    const newSummaries = (summaries || []).map(s => s.pathKey === summary.pathKey ? summary : s);
    if (!newSummaries.some(s => s.pathKey === summary.pathKey)) {
        newSummaries.push(summary);
    }
    return newSummaries;
}

// removeSummary removes the summary aggregating the given path key.
export function removeSummary(summaries: Summary[] | null, pathKey: string): Summary[] {
    return (summaries || []).filter(s => s.pathKey !== pathKey);
}

// summaryAggregationsByColumns maps the view summaries onto the response
// columns: one entry per column (null when the column is not summarized),
// index-aligned with the columns and with the summaries value arrays. Pivoted
// columns share the path key of their value field and all get its aggregation.
export function summaryAggregationsByColumns(summaries: Summary[] | null | undefined, fieldColumns: FieldColumn[]): (AggregationType | null)[] {
    if (!summaries || summaries.length === 0) {
        return fieldColumns.map(() => null);
    }
    const aggregationByPathKeys = new Map(summaries.map(s => [s.pathKey, s.aggregation]));
    return fieldColumns.map(fieldColumn => (fieldColumn.pathKey && aggregationByPathKeys.get(fieldColumn.pathKey)) || null);
}

// summaryValueToString formats a raw summary value the way the grid formats
// the column cells (view format first, then the field format). aggregation is
// the summary aggregation (SUM/AVG/MIN/MAX), columnAggregation the aggregation
// of the column itself: when it changes the displayed type (a COUNT over a
// text field displays integers), the total is formatted as that type and the
// field/view format — a format for the declared type — does not apply.
export function summaryValueToString(value: any, fieldColumn: FieldColumn, aggregation: AggregationType, columnAggregation: AggregationType | undefined, formats: Map<string, Map<string, any>> | null | undefined, fieldByKeys: Map<string, Field> | null, t: TFunction): string {
    if (value === null || value === undefined) {
        return "";
    }
    let field = fieldColumn.field;
    let format = formats?.get(fieldColumn.pathKey ?? "") ?? field.format;
    const outputType = summaryOutputType(field, columnAggregation, fieldByKeys);
    if (outputType !== field.type) {
        field = { ...field, type: outputType };
        format = undefined;
    }
    if (aggregation === AggregationType.AVG && field.type === FieldType.Number) {
        // The average of integers is not an integer: display it as a decimal
        field = { ...field, type: FieldType.Decimal };
    }
    return valueToString(value, field, aggregation, format, t, "", fieldByKeys);
}

// shouldRefreshSummaries tells whether the totals may be re-asked to the
// backend after a light mutation. False when the view has no summaries or
// when the totals are not displayed yet (loading in progress: the last page
// will bring them). Pivot views refresh too — including after filling an
// empty pivot cell, which duplicates the row's record (fillPivot): the totals
// then include every cloned value and may briefly be ahead of the displayed
// rows, which catch up at the next full fetch.
export function shouldRefreshSummaries(view: View | null | undefined, currentSummaries: (any | null)[] | null): boolean {
    if (!view?.summaries?.length) {
        return false;
    }
    if (currentSummaries === null) {
        return false;
    }
    return true;
}

// applyGroupSummaries applies the group summaries of a summaries-only
// response (the group skeleton, without record lines) onto the loaded group
// lines, by position: the skeleton was computed from the same view request,
// so the trees have the same shape as long as the mutation did not change the
// grouping — structural edits go through a full refetch instead. A level
// whose shape drifted is left untouched (the next full fetch reconciles).
// Group lines are rebuilt as new objects so the memoized group rows
// re-render. All the values are computed by the backend: the front never
// aggregates anything itself.
export function applyGroupSummaries(lines: Line[], skeleton: Line[], groupDepth: number, level: number = 0): Line[] {
    if (level >= groupDepth || lines.length === 0) {
        return lines;
    }
    if (lines.length !== skeleton.length) {
        return lines;
    }
    return lines.map((line, index) => ({
        ...line,
        summaries: skeleton[index].summaries ?? line.summaries,
        children: applyGroupSummaries(line.children ?? [], skeleton[index].children ?? [], groupDepth, level + 1),
    }));
}

// summaryCellRadius computes the border radius of a summary band cell so that
// CONTIGUOUS summarized cells merge into one continuous band, rounded only on
// its BOTTOM extremities: bottom-left corner when the previous column is not
// part of the band, bottom-right corner when the next one is not. The top
// corners stay square.
export function summaryCellRadius(summarized: boolean, previousSummarized: boolean, nextSummarized: boolean, radius: number = 8): string | undefined {
    if (!summarized) {
        return undefined;
    }
    const left = previousSummarized ? 0 : radius;
    const right = nextSummarized ? 0 : radius;
    return `0px 0px ${right}px ${left}px`;
}

// summaryRowKeys computes the React keys of the summary editor rows: one key
// per summary plus the key of the trailing pristine row used to add a summary.
// The pristine row key embeds the list length so the row remounts empty after
// each add or remove: its selection lives in local state, which React keeps as
// long as the key is unchanged, and cannot be reset from props.
export function summaryRowKeys(prefix: string, summaries: Summary[] | null): { itemKeys: string[], newRowKey: string } {
    const list = summaries || [];
    return {
        itemKeys: list.map(s => prefix + s.pathKey),
        newRowKey: prefix + "new-" + list.length,
    };
}
